<template>
  <div class="round-sizes-card">
    <div class="round-sizes-header">
      <i class="fa-solid fa-list-ol" aria-hidden="true"></i>
      <span>{{ $t('competitions.view.round_sizes') }}</span>
      <button
        v-if="activeStages.length"
        class="info-btn"
        type="button"
        aria-label="Ver ejemplos"
        @click="openExamples"
      >
        <i class="fa-solid fa-circle-info" aria-hidden="true"></i>
      </button>
    </div>
    <div v-if="sortedStages.length" class="carousel-shell">
      <button class="nav-btn left" type="button" aria-label="Anterior" @click="scrollCarousel(-1)">
        <i class="fa-solid fa-chevron-left" aria-hidden="true"></i>
      </button>
      <div class="round-sizes-carousel" ref="carousel" role="list">
        <div
          v-for="stage in sortedStages"
          :key="stage.id"
          class="stage-card"
          :class="{ inactive: hasActive(stage) && !stage.active, disabled: allLocked, readonly: !canControlStages }"
          role="listitem"
          @click="handleStageClick(stage)"
        >
          <div class="pill-header">
            <span class="pill-label">{{ stageLabel(stage) }}</span>
          </div>
          <div class="pill-footer">
            <div class="status-block">
              <span class="status-dot" :class="stage.active ? 'on' : 'off'"></span>
              <span class="status-text">{{ stage.active ? 'Activa' : 'Inactiva' }}</span>
            </div>
            <span class="pill-circle" v-if="stage?.athlete_count">{{ stage.athlete_count }}</span>
          </div>
        </div>
      </div>
      <button class="nav-btn right" type="button" aria-label="Siguiente" @click="scrollCarousel(1)">
        <i class="fa-solid fa-chevron-right" aria-hidden="true"></i>
      </button>
    </div>
    <div v-else class="round-sizes-empty">
      {{ $t('competitions.view.round_sizes_empty') }}
    </div>

    <div v-if="infoModalOpen && previewStage" class="preview-backdrop" @click.self="closeExamples">
      <div class="preview-modal preview-card">
        <div class="preview-modal-header">
          <span class="preview-title">{{ stageLabel(previewStage) }}</span>
          <button class="preview-close" @click="closeExamples" aria-label="Cerrar">✕</button>
        </div>
        <div class="example-tabs" role="tablist">
                <button
                  v-for="stage in activeStages"
                  :key="stage.id"
                  class="example-tab"
                  :class="{ active: previewStage && stage.id === previewStage.id }"
                  type="button"
                  role="tab"
                  @click="setExampleStage(stage)"
                >
                  {{ stageLabel(stage) }}
                </button>
        </div>
        <div class="preview-stage-card">
          <div class="preview-stage-header">
            <span class="pill">{{ stageLabel(previewStage) }}</span>
            <span class="badge-mode">{{ isVs(previewStage) ? 'VS' : 'Scoring' }}</span>
          </div>
          <div class="preview-meta">
            <span class="pill-circle">{{ previewStage?.athlete_count || '—' }}</span>
            <span class="status-text" :class="previewStage.active ? 'on' : 'off'">
              {{ previewStage.active ? 'Activa' : 'Inactiva' }}
            </span>
          </div>

          <div v-if="isVs(previewStage)" class="preview-bracket">
            <div
              v-if="previewBracketBlocks.length"
              :class="[
                'preview-bracket__stack',
                { 'preview-bracket__stack--split': previewBracketBlocks.length > 1 }
              ]"
            >
              <section
                v-for="block in previewBracketBlocks"
                :key="block.key"
                :class="['preview-bracket__layout', `preview-bracket__layout--${block.layout}`]"
              >
                <template v-if="block.layout === 'single'">
                  <div class="preview-bracket__center">
                    <div class="preview-bracket__column preview-bracket__column--center">
                      <header
                        v-if="block.label"
                        class="preview-bracket__column-header preview-bracket__column-header--center"
                      >
                        <span>{{ block.label }}</span>
                      </header>

                      <div class="preview-bracket__matches preview-bracket__matches--center">
                        <article
                          v-for="match in block.allMatches"
                          :key="match.key"
                          class="preview-bracket__match"
                        >
                          <div class="preview-bracket__slots">
                            <div
                              v-for="(seed, idx) in match.seeds"
                              :key="`${match.key}-${seed}`"
                              class="preview-bracket__slot"
                              :class="previewSlotClasses(match, idx)"
                            >
                              <span class="preview-bracket__avatar-wrap">
                                <img :src="defaultAvatar" alt="avatar" class="preview-bracket__avatar" />
                                <span v-if="isPreviewWinner(match, idx)" class="preview-bracket__winner-mark" aria-hidden="true">
                                  <i class="fa-solid fa-crown"></i>
                                </span>
                              </span>

                              <span class="preview-bracket__slot-copy">
                                <strong class="preview-bracket__slot-name">Seed {{ seed }}</strong>
                                <span
                                  class="preview-bracket__status-pill"
                                  :class="isPreviewWinner(match, idx) ? 'preview-bracket__status-pill--winner' : 'preview-bracket__status-pill--published'"
                                >
                                  {{ previewStatusLabel(match, idx) }}
                                </span>
                              </span>

                              <strong class="preview-bracket__slot-score">{{ dummyScore(seed) }}</strong>
                            </div>
                          </div>
                        </article>
                      </div>
                    </div>
                  </div>
                </template>

                <template v-else>
                  <section class="preview-bracket__side preview-bracket__side--left">
                    <div class="preview-bracket__column">
                      <div class="preview-bracket__match-groups">
                        <div
                          v-for="group in buildPreviewConnectorGroups(block.leftMatches, 'left')"
                          :key="group.key"
                          class="preview-bracket__match-group"
                        >
                          <div class="preview-bracket__match-group-body">
                            <article
                              v-for="match in group.matches"
                              :key="match.key"
                              class="preview-bracket__match"
                            >
                              <div class="preview-bracket__slots">
                                <div
                                  v-for="(seed, idx) in match.seeds"
                                  :key="`${match.key}-${seed}`"
                                  class="preview-bracket__slot"
                                  :class="previewSlotClasses(match, idx)"
                                >
                                  <span class="preview-bracket__avatar-wrap">
                                    <img :src="defaultAvatar" alt="avatar" class="preview-bracket__avatar" />
                                    <span v-if="isPreviewWinner(match, idx)" class="preview-bracket__winner-mark" aria-hidden="true">
                                      <i class="fa-solid fa-crown"></i>
                                    </span>
                                  </span>

                                  <span class="preview-bracket__slot-copy">
                                    <strong class="preview-bracket__slot-name">Seed {{ seed }}</strong>
                                    <span
                                      class="preview-bracket__status-pill"
                                      :class="isPreviewWinner(match, idx) ? 'preview-bracket__status-pill--winner' : 'preview-bracket__status-pill--published'"
                                    >
                                      {{ previewStatusLabel(match, idx) }}
                                    </span>
                                  </span>

                                  <strong class="preview-bracket__slot-score">{{ dummyScore(seed) }}</strong>
                                </div>
                              </div>
                            </article>
                          </div>

                          <div class="preview-bracket__connector preview-bracket__connector--left" aria-hidden="true">
                            <div
                              :class="[
                                'preview-bracket__connector-segment',
                                group.matches.length > 1
                                  ? 'preview-bracket__connector-segment--branch'
                                  : 'preview-bracket__connector-segment--single'
                              ]"
                            >
                              <span class="preview-bracket__connector-line preview-bracket__connector-line--top"></span>
                              <span class="preview-bracket__connector-line preview-bracket__connector-line--bottom"></span>
                              <span class="preview-bracket__connector-spine"></span>
                              <span class="preview-bracket__connector-line preview-bracket__connector-line--out"></span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section class="preview-bracket__side preview-bracket__side--right">
                    <div class="preview-bracket__column">
                      <div class="preview-bracket__match-groups">
                        <div
                          v-for="group in buildPreviewConnectorGroups(block.rightMatches, 'right')"
                          :key="group.key"
                          class="preview-bracket__match-group preview-bracket__match-group--right"
                        >
                          <div class="preview-bracket__connector preview-bracket__connector--right" aria-hidden="true">
                            <div
                              :class="[
                                'preview-bracket__connector-segment',
                                group.matches.length > 1
                                  ? 'preview-bracket__connector-segment--branch'
                                  : 'preview-bracket__connector-segment--single'
                              ]"
                            >
                              <span class="preview-bracket__connector-line preview-bracket__connector-line--top"></span>
                              <span class="preview-bracket__connector-line preview-bracket__connector-line--bottom"></span>
                              <span class="preview-bracket__connector-spine"></span>
                              <span class="preview-bracket__connector-line preview-bracket__connector-line--out"></span>
                            </div>
                          </div>

                          <div class="preview-bracket__match-group-body">
                            <article
                              v-for="match in group.matches"
                              :key="match.key"
                              class="preview-bracket__match"
                            >
                              <div class="preview-bracket__slots">
                                <div
                                  v-for="(seed, idx) in match.seeds"
                                  :key="`${match.key}-${seed}`"
                                  class="preview-bracket__slot"
                                  :class="previewSlotClasses(match, idx)"
                                >
                                  <span class="preview-bracket__avatar-wrap">
                                    <img :src="defaultAvatar" alt="avatar" class="preview-bracket__avatar" />
                                    <span v-if="isPreviewWinner(match, idx)" class="preview-bracket__winner-mark" aria-hidden="true">
                                      <i class="fa-solid fa-crown"></i>
                                    </span>
                                  </span>

                                  <span class="preview-bracket__slot-copy">
                                    <strong class="preview-bracket__slot-name">Seed {{ seed }}</strong>
                                    <span
                                      class="preview-bracket__status-pill"
                                      :class="isPreviewWinner(match, idx) ? 'preview-bracket__status-pill--winner' : 'preview-bracket__status-pill--published'"
                                    >
                                      {{ previewStatusLabel(match, idx) }}
                                    </span>
                                  </span>

                                  <strong class="preview-bracket__slot-score">{{ dummyScore(seed) }}</strong>
                                </div>
                              </div>
                            </article>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                </template>
              </section>
            </div>
          </div>

          <div v-else-if="previewScoring.rows.length" class="preview-scoring">
            <div v-if="!previewScoring.isFinalStage" class="preview-advances">Avanzan {{ previewScoring.advances }}</div>
            <div v-if="!previewScoring.isFinalStage" class="preview-eliminated">Eliminados desde {{ previewScoring.eliminateFrom }}</div>
            <div v-else class="preview-advances">Posiciones finales</div>
            <div class="scoring-list">
              <div
                v-for="(athlete, idx) in scoringVisibleRows(previewScoring)"
                :key="athlete.seed"
                class="scoring-row"
                :class="{ advance: !previewScoring.isFinalStage && idx < previewScoring.advances, out: !previewScoring.isFinalStage && idx >= previewScoring.advances }"
              >
                <div class="rank">{{ idx + 1 }}</div>
                <div class="scoring-avatar"><img :src="defaultAvatar" alt="avatar" class="scoring-img" /></div>
                <div class="scoring-name">Seed {{ athlete.seed }}</div>
                <div class="scoring-score">{{ athlete.score }}</div>
                <span
                  v-if="!previewScoring.isFinalStage"
                  class="scoring-badge"
                  :class="{ muted: idx >= previewScoring.advances }"
                >{{ idx < previewScoring.advances ? 'Avanza' : 'Eliminado' }}</span>
                <span v-else class="scoring-badge neutral">Final</span>
              </div>
            </div>
            <button
              v-if="hasScoringOverflow(previewScoring)"
              class="scoring-toggle"
              @click="toggleScoringExpanded(previewScoring.value)"
            >
              {{ scoringExpanded[previewScoring.value] ? 'Ver menos' : 'Ver más' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <ConfirmationModal
      ref="blockedModal"
      :message="blockMessage"
      :showCancel="false"
      @confirmed="handleBlockedConfirm"
    />
  </div>
</template>

<script>
import defaultAvatar from "@/assets/user.png";
import ConfirmationModal from "@/components/competitions/ConfirmationModal.vue";

export default {
  name: "RoundSizesList",
  components: { ConfirmationModal },
  props: {
    roundSizes: {
      type: Array,
      default: () => []
    },
    isVsCompetition: {
      type: Boolean,
      default: false
    },
    canControlStages: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      previewStage: null,
      scoringExpanded: {},
      defaultAvatar,
      infoModalOpen: false,
      blockMessage: ''
    };
  },
  computed: {
    allLocked() {
      return this.sortedStages.length > 0 && this.sortedStages.every((s) => !!s.locked);
    },
    sortedStages() {
      return [...this.roundSizes].sort((a, b) => (a?.id || 0) - (b?.id || 0));
    },
    activeStages() {
      return this.sortedStages.filter((stage) => !this.hasActive(stage) || stage.active);
    },
    previewMatches() {
      const stage = this.previewStage;
      if (!stage || !this.isVs(stage)) return [];
      const stageValue = Number(stage?.id) || 0;
      const total = Number(stage?.athlete_count) || this.getStageAthleteCount(stageValue);
      return this.buildVsMatches(stageValue, total);
    },
    previewGroups() {
      const stageValue = Number(this.previewStage?.id) || 0;
      return this.groupMatches(stageValue, this.previewMatches);
    },
    previewBracketBlocks() {
      if (!this.previewStage || !this.isVs(this.previewStage) || !this.previewMatches.length) {
        return [];
      }

      const hasThirdPlaceMatch = this.previewMatches.some((match) => match.group === '3P');

      if (!hasThirdPlaceMatch) {
        return [this.buildPreviewBracketBlock('main', '', this.previewMatches)];
      }

      return [
        this.buildPreviewBracketBlock(
          'final',
          this.$t('rounds.groups.final.first_second'),
          this.previewMatches.filter((match) => match.group === 'F')
        ),
        this.buildPreviewBracketBlock(
          'third_place',
          this.$t('rounds.groups.final.third_fourth'),
          this.previewMatches.filter((match) => match.group === '3P')
        )
      ].filter((block) => block.allMatches.length);
    },
    previewScoring() {
      const stage = this.previewStage;
      if (!stage) return { rows: [], advances: 0, isFinalStage: false, value: null };
      return this.scoringPreview(stage);
    }
  },
  methods: {
    handleStageClick(stage) {
      if (!this.canControlStages) return;
      this.toggleStage(stage);
    },
    stageLabel(stage) {
      if (!stage) return "";
      const key = `rounds.stage${stage.id}`;
      return this.$te(key) ? this.$t(key) : stage.name || "";
    },
    hasActive(stage) {
      return stage && Object.prototype.hasOwnProperty.call(stage, "active");
    },
    isVs(stage) {
      if (!stage) return false;
      if (stage.type) return stage.type === "vs";
      if (stage.mode) return stage.mode === "vs";
      if (Object.prototype.hasOwnProperty.call(stage, "is_vs")) return !!stage.is_vs;
      return this.isVsCompetition;
    },
    setExampleStage(stage) {
      if (!stage) return;
      if (this.hasActive(stage) && !stage.active) return;
      this.previewStage = stage;
    },
    openExamples() {
      if (!this.activeStages.length) return;
      const current = this.previewStage;
      const fallback = this.activeStages[0];
      this.previewStage = current && (!this.hasActive(current) || current.active)
        ? current
        : fallback;
      this.infoModalOpen = true;
    },
    closeExamples() {
      this.infoModalOpen = false;
    },
    buildVsMatches(stageValue, count) {
      if (!count || count < 2) return [];
      const stage = Number(stageValue);
      const withGroups = (pairs, groups = []) => pairs.map((seeds, index) => ({
        number: index + 1,
        seeds,
        group: groups[index] || '',
        key: `${groups[index] || 'stage'}:${index + 1}`
      }));

      if (stage === 1 && count === 4) {
        return [
          { number: 1, seeds: [1, 5], titleKey: 'rounds.groups.final.first_second', group: 'F', key: 'F:1' },
          { number: 2, seeds: [2, 6], titleKey: 'rounds.groups.final.third_fourth', group: '3P', key: '3P:2' }
        ];
      }

      if (stage === 2 && count === 4) {
        const pairs = [
          [1, 2],
          [5, 6]
        ];
        return withGroups(pairs, ['A', 'B']);
      }

      if (stage === 3 && count === 8) {
        const pairs = [
          [1, 4],
          [2, 3],
          [8, 5],
          [7, 6]
        ];
        return withGroups(pairs, ['A', 'A', 'B', 'B']);
      }

      if (stage === 4 && count === 16) {
        const pairs = [
          [1, 16], [4, 13],
          [8, 9],  [5, 12],
          [2, 15], [3, 14],
          [7, 10], [6, 11]
        ];
        return withGroups(pairs, ['A', 'A', 'B', 'B', 'C', 'C', 'D', 'D']);
      }

      if (stage === 5 && count === 32) {
        const pairs = [
          [1, 32], [16, 17],
          [8, 25], [9, 24],
          [5, 28], [12, 21],
          [4, 29], [13, 20],
          [3, 30], [14, 19],
          [6, 27], [11, 22],
          [7, 26], [10, 23],
          [15, 18], [2, 31]
        ];
        return withGroups(pairs, ['A', 'A', 'B', 'B', 'C', 'C', 'D', 'D', 'E', 'E', 'F', 'F', 'G', 'G', 'H', 'H']);
      }

      const matches = [];
      let left = 1;
      let right = count;
      let matchIndex = 1;
      while (left < right) {
        matches.push({ number: matchIndex, seeds: [left, right], group: '', key: `stage:${matchIndex}` });
        left += 1;
        right -= 1;
        matchIndex += 1;
      }
      return matches;
    },
    buildPreviewBracketBlock(key, label, matches) {
      const allMatches = [...matches];
      const sideMap = this.buildPreviewMatchSideMap(allMatches);
      const leftMatches = allMatches.filter((match, index) => this.resolvePreviewMatchSide(match, index, allMatches.length, sideMap) === 'left');
      const rightMatches = allMatches.filter((match, index) => this.resolvePreviewMatchSide(match, index, allMatches.length, sideMap) === 'right');
      const layout = allMatches.length <= 1 || !leftMatches.length || !rightMatches.length ? 'single' : 'paired';

      return {
        key,
        label,
        layout,
        allMatches,
        leftMatches,
        rightMatches
      };
    },
    buildPreviewMatchSideMap(matches) {
      const orderedGroups = [];

      (matches || []).forEach((match) => {
        const normalizedGroup = String(match?.group || '').trim();
        if (normalizedGroup && !orderedGroups.includes(normalizedGroup)) {
          orderedGroups.push(normalizedGroup);
        }
      });

      if (orderedGroups.length <= 1) {
        return null;
      }

      const boundary = Math.ceil(orderedGroups.length / 2);
      return new Map(
        orderedGroups.map((group, index) => [group, index < boundary ? 'left' : 'right'])
      );
    },
    buildPreviewConnectorGroups(matches, side) {
      const groups = [];

      for (let index = 0; index < matches.length; index += 2) {
        groups.push({
          key: `${side}-${index}`,
          matches: matches.slice(index, index + 2)
        });
      }

      return groups;
    },
    resolvePreviewMatchSide(match, index, totalMatches, sideMap = null) {
      const normalizedGroup = String(match?.group || '').trim().toLowerCase();

      const originalGroup = String(match?.group || '').trim();
      if (sideMap && originalGroup && sideMap.has(originalGroup)) {
        return sideMap.get(originalGroup);
      }

      if (normalizedGroup) {
        const numericGroup = Number.parseInt(normalizedGroup, 10);
        if (Number.isFinite(numericGroup)) {
          return numericGroup <= Math.ceil(totalMatches / 2) ? 'left' : 'right';
        }
      }

      return index < Math.ceil(totalMatches / 2) ? 'left' : 'right';
    },
    previewSlotClasses(match, index) {
      return {
        'preview-bracket__slot--winner': this.isPreviewWinner(match, index)
      };
    },
    previewStatusLabel(match, index) {
      return this.isPreviewWinner(match, index) ? 'Ganador' : 'Publicado';
    },
    previewGroupLayout(stageValue, matches) {
      const groups = this.groupMatches(stageValue, matches);
      const count = groups.length;
      if (count <= 2) return "grid-2";
      if (stageValue === 4 && count === 4) return "grid-2-rows";
      if (stageValue === 5) return "grid-4-wide";
      if (count <= 4) return "grid-4";
      return "grid-auto";
    },
    groupMatches(stageValue, matches) {
      if (!Array.isArray(matches) || !matches.length) return [];
      const stage = Number(stageValue);
      const groups = [];

      if (stage === 1) {
        groups.push({ name: this.$t('rounds.groups.final.first_second'), matches: matches.slice(0, 1) });
        groups.push({ name: this.$t('rounds.groups.final.third_fourth'), matches: matches.slice(1, 2) });
      } else if (stage === 2) {
        const letters = ['A', 'B'];
        groups.push({ name: this.$t('rounds.groups.group', { group: letters[0] }), matches: matches.slice(0, 1) });
        groups.push({ name: this.$t('rounds.groups.group', { group: letters[1] }), matches: matches.slice(1, 2) });
      } else if (stage === 3) {
        const letters = ['A', 'B', 'C', 'D'];
        groups.push({ name: this.$t('rounds.groups.group', { group: letters[0] }), matches: matches.slice(0, 2) });
        groups.push({ name: this.$t('rounds.groups.group', { group: letters[1] }), matches: matches.slice(2, 4) });
      } else if (stage === 4) {
        const letters = ['A', 'B', 'C', 'D'];
        for (let i = 0; i < 4; i += 1) {
          groups.push({ name: this.$t('rounds.groups.group', { group: letters[i] }), matches: matches.slice(i * 2, i * 2 + 2) });
        }
      } else if (stage === 5) {
        const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
        for (let i = 0; i < 8; i += 1) {
          groups.push({ name: this.$t('rounds.groups.group', { group: letters[i] }), matches: matches.slice(i * 2, i * 2 + 2) });
        }
      } else {
        groups.push({ name: '', matches });
      }

      return groups.filter((group) => group.matches.length);
    },
    getStageAthleteCount(value) {
      const map = { 1: 4, 2: 4, 3: 8, 4: 16, 5: 32 };
      return map[value] || 0;
    },
    dummyScore(seed) {
      const score = 90 - seed * 1.2;
      return score.toFixed(1);
    },
    previewScore(seed) {
      const value = parseFloat(this.dummyScore(seed));
      return Number.isFinite(value) ? value : 0;
    },
    isPreviewWinner(match, index) {
      if (!match || !Array.isArray(match.seeds) || match.seeds.length < 2) return false;
      const scoreA = this.previewScore(match.seeds[0]);
      const scoreB = this.previewScore(match.seeds[1]);
      if (scoreA === scoreB) return false;
      return index === 0 ? scoreA > scoreB : scoreB > scoreA;
    },
    scoringPreview(stage) {
      const stageValue = Number(stage?.id) || 0;
      const advances = stageValue === 1 ? 0 : Number(stage?.athlete_count) || 0;
      const total = Math.max(advances * 2, advances + 2, Math.max(4, Number(stage?.athlete_count) || 0));
      const rows = [];

      for (let i = 0; i < total; i += 1) {
        const seed = i + 1;
        const score = (total - i) * 9.6;
        rows.push({ seed, score: score.toFixed(1) });
      }

      return {
        value: stageValue,
        rows,
        advances,
        eliminateFrom: advances ? advances + 1 : null,
        isFinalStage: stageValue === 1
      };
    },
    scoringVisibleRows(stage) {
      const limit = 5;
      if (!stage || !stage.rows) return [];
      if (stage.rows.length <= limit) return stage.rows;
      return this.scoringExpanded[stage.value] ? stage.rows : stage.rows.slice(0, limit);
    },
    hasScoringOverflow(stage) {
      const limit = 5;
      if (!stage || !stage.rows) return false;
      return stage.rows.length > limit;
    },
    toggleScoringExpanded(stageValue) {
      const current = !!this.scoringExpanded[stageValue];
      this.scoringExpanded = { ...this.scoringExpanded, [stageValue]: !current };
    },
    toggleStage(stage) {
      if (this.allLocked) {
        const msg = this.$t?.('competitions.rounds.deactivate_blocked') || 'No puedes desactivar una etapa intermedia porque hay una posterior activa.';
        this.blockMessage = msg;
        this.$refs.blockedModal?.openModal();
        return;
      }
      this.$emit('toggle-stage', stage);
    },
    handleBlockedConfirm() {
      this.$refs.blockedModal?.closeModal();
    },
    scrollCarousel(direction) {
      const el = this.$refs.carousel;
      if (!el) return;
      const firstCard = el.querySelector(".stage-card");
      const amount = firstCard ? firstCard.getBoundingClientRect().width + 10 : 250;
      el.scrollBy({ left: direction * amount, behavior: "smooth" });
    }
  }
};
</script>

<style scoped>
.round-sizes-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px;
  background: #fff;
  position: relative;
  overflow: visible;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.08);
}
.round-sizes-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  margin-bottom: 8px;
  color: #1f2937;
}
.info-btn {
  margin-left: auto;
  border: 1px solid #dfe3ec;
  background: #ffffff;
  color: #0f172a;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.15s ease;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
}
.info-btn:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
  transform: translateY(-1px);
}
.info-btn i {
  font-size: 16px;
}
.carousel-shell {
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 10px;
}
.round-sizes-carousel {
  display: flex;
  gap: 10px;
  overflow: hidden;
  padding: 4px 6px;
  flex: 1;
  flex-wrap: nowrap;
}
.stage-card {
  flex: 0 0 180px;
  min-width: 160px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 8px 10px;
  background: linear-gradient(145deg, #f8fafc, #ffffff);
  color: #0f172a;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, outline 0.15s ease;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.08);
}
.stage-card.readonly {
  cursor: default;
  pointer-events: none;
}
.stage-card:hover {
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
  outline: 2px solid #4c6fff;
}
.stage-card.inactive {
  opacity: 0.55;
  cursor: default;
}
.stage-card.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
.nav-btn {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1px solid #dfe3ec;
  background: #ffffff;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.15s ease;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.1);
}
.nav-btn:hover:not(:disabled) {
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.14);
  transform: translateY(-1px);
}
.nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  box-shadow: none;
}
.nav-btn i {
  color: #111827;
}
.pill-header {
  display: flex;
  align-items: center;
  gap: 8px;
}
.pill-label {
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
  word-break: break-word;
}
.pill {
  background: #eef2ff;
  color: #3d4ea1;
  padding: 4px 8px;
  border-radius: 999px;
  font-weight: 600;
}
.pill-circle {
  margin-left: auto;
  min-width: 24px;
  text-align: center;
  padding: 3px 6px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #111827;
  font-weight: 700;
  font-size: 11px;
  border: 1px solid #e2e8f0;
}
.pill-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  color: #475569;
  font-size: 12px;
  margin-top: auto;
}
.status-block {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #a12626;
}
.status-dot.on {
  background: #22c55e;
}
.status-text.on {
  color: #15803d;
}
.status-text.off {
  color: #b91c1c;
}
.round-sizes-empty {
  color: #6b7280;
  font-style: italic;
}

.preview-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 900;
  padding: 16px;
}
.preview-modal {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  width: min(560px, 92vw);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 90vh;
  overflow-y: auto;
}
.preview-card {
  border: 1px solid #e3e3e3;
  border-radius: 8px;
  padding: 12px;
  background: #f9f9ff;
}
.preview-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.example-tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 6px;
}
.example-tab {
  border: 1px solid #dfe3ec;
  background: #f8fafc;
  color: #0f172a;
  border-radius: 999px;
  padding: 6px 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}
.example-tab.active {
  background: #4c6fff;
  color: #fff;
  border-color: #4c6fff;
  box-shadow: 0 8px 18px rgba(76, 111, 255, 0.25);
}
.example-tab:hover:not(.active) {
  background: #eef2ff;
}
.preview-title {
  font-weight: 800;
  font-size: 18px;
}
.preview-close {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
}
.preview-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0.35rem 0;
}
.preview-stage-card {
  border: 1px solid #dfe3f0;
  border-radius: 8px;
  padding: 10px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.preview-stage-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.badge-mode {
  background: #eef1ff;
  color: #4c56af;
  padding: 3px 8px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 12px;
}
.preview-bracket {
  width: 100%;
}

.preview-bracket__stack {
  display: grid;
  gap: 0.95rem;
}

.preview-bracket__stack--split {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: start;
}

.preview-bracket__layout {
  display: grid;
  gap: 0.95rem;
}

.preview-bracket__layout--paired {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}

.preview-bracket__side {
  display: flex;
}

.preview-bracket__side--left {
  justify-content: flex-end;
}

.preview-bracket__side--right {
  justify-content: flex-start;
}

.preview-bracket__center {
  display: flex;
  justify-content: center;
}

.preview-bracket__column {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  min-width: 0;
  flex: 1 1 0;
}

.preview-bracket__column--center {
  width: 100%;
}

.preview-bracket__column-header {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.7rem 0.85rem;
  border-radius: 0.95rem;
  background: #eff6ff;
  box-shadow: inset 0 0 0 1px rgba(59, 130, 246, 0.12);
  color: #0f172a;
  font-weight: 800;
  text-align: center;
}

.preview-bracket__column-header--center {
  background: #ecfeff;
  box-shadow: inset 0 0 0 1px rgba(20, 184, 166, 0.15);
}

.preview-bracket__matches,
.preview-bracket__match-group-body {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.preview-bracket__matches--center {
  justify-content: center;
}

.preview-bracket__match-groups {
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}

.preview-bracket__match-group {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 1.6rem;
  align-items: stretch;
  gap: 0.35rem;
}

.preview-bracket__match-group--right {
  grid-template-columns: 1.6rem minmax(0, 1fr);
}

.preview-bracket__match {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  padding: 0.85rem;
  border-radius: 1.1rem;
  background: linear-gradient(180deg, #dbeafe, #bfdbfe);
  box-shadow: inset 0 0 0 1px rgba(59, 130, 246, 0.18), 0 14px 28px rgba(37, 99, 235, 0.14);
}

.preview-bracket__slots {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.preview-bracket__slot {
  width: 100%;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.65rem;
  padding: 0.55rem 0.7rem;
  border-radius: 999px;
  background: linear-gradient(180deg, #ffffff, #f8fafc);
  color: #0f172a;
  text-align: left;
  box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.18), 0 8px 18px rgba(15, 23, 42, 0.05);
}

.preview-bracket__slot--winner {
  background: linear-gradient(90deg, rgba(34, 197, 94, 0.22), rgba(255, 255, 255, 0.98));
  color: #14532d;
  box-shadow: inset 0 0 0 1px rgba(22, 163, 74, 0.26), 0 10px 22px rgba(34, 197, 94, 0.16);
}

.preview-bracket__avatar-wrap {
  position: relative;
  width: 2.55rem;
  height: 2.55rem;
  flex: 0 0 2.55rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.preview-bracket__avatar {
  width: 100%;
  height: 100%;
  border-radius: 999px;
  object-fit: cover;
  border: 2px solid #e2e8f0;
}

.preview-bracket__slot--winner .preview-bracket__avatar {
  border-color: #22c55e;
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.18), 0 0 14px rgba(34, 197, 94, 0.14);
}

.preview-bracket__winner-mark {
  position: absolute;
  right: -0.12rem;
  bottom: -0.12rem;
  width: 1.15rem;
  height: 1.15rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(21, 128, 61, 0.92);
  color: #f0fdf4;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.92), 0 8px 18px rgba(22, 163, 74, 0.35);
  font-size: 0.58rem;
  z-index: 2;
}

.preview-bracket__slot-copy {
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
}

.preview-bracket__slot-name {
  min-width: 0;
  flex: 1 1 8rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.82rem;
  font-weight: 700;
}

.preview-bracket__slot-score {
  margin-left: auto;
  min-width: 3rem;
  text-align: right;
  font-size: 0.82rem;
  color: #0f172a;
  font-weight: 800;
}

.preview-bracket__slot--winner .preview-bracket__slot-name,
.preview-bracket__slot--winner .preview-bracket__slot-score {
  color: #166534;
}

.preview-bracket__status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 1.45rem;
  padding: 0.16rem 0.58rem;
  border-radius: 999px;
  font-size: 0.66rem;
  font-weight: 800;
  white-space: nowrap;
}

.preview-bracket__status-pill--winner {
  background: #dcfce7;
  color: #15803d;
}

.preview-bracket__status-pill--published {
  background: #ede9fe;
  color: #6d28d9;
}

.preview-bracket__connector {
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: center;
}

.preview-bracket__connector-segment {
  position: relative;
  flex: 1 1 auto;
  width: 100%;
}

.preview-bracket__connector-line,
.preview-bracket__connector-spine {
  position: absolute;
  background: rgba(148, 163, 184, 0.62);
}

.preview-bracket__connector-segment--branch .preview-bracket__connector-line--top,
.preview-bracket__connector-segment--branch .preview-bracket__connector-line--bottom {
  width: 0.8rem;
  height: 2px;
}

.preview-bracket__connector-segment--branch .preview-bracket__connector-line--top {
  top: 25%;
}

.preview-bracket__connector-segment--branch .preview-bracket__connector-line--bottom {
  top: 75%;
}

.preview-bracket__connector-segment--branch .preview-bracket__connector-spine {
  top: 25%;
  bottom: 25%;
  width: 2px;
}

.preview-bracket__connector-segment--branch .preview-bracket__connector-line--out {
  top: 50%;
  width: 0.8rem;
  height: 2px;
}

.preview-bracket__connector-segment--single .preview-bracket__connector-line--top,
.preview-bracket__connector-segment--single .preview-bracket__connector-line--bottom,
.preview-bracket__connector-segment--single .preview-bracket__connector-spine {
  display: none;
}

.preview-bracket__connector-segment--single .preview-bracket__connector-line--out {
  top: 50%;
  width: 100%;
  height: 2px;
}

.preview-bracket__connector--left .preview-bracket__connector-line--top,
.preview-bracket__connector--left .preview-bracket__connector-line--bottom,
.preview-bracket__connector--left .preview-bracket__connector-spine {
  right: 0.8rem;
}

.preview-bracket__connector--left .preview-bracket__connector-line--out {
  left: 0.8rem;
}

.preview-bracket__connector--right .preview-bracket__connector-line--top,
.preview-bracket__connector--right .preview-bracket__connector-line--bottom,
.preview-bracket__connector--right .preview-bracket__connector-spine {
  left: 0.8rem;
}

.preview-bracket__connector--right .preview-bracket__connector-line--out {
  right: 0.8rem;
}
.preview-matches {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
  margin-top: 6px;
}
.preview-matches.grid-2 {
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}
.preview-matches.grid-4 {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
}
.preview-matches.grid-2-rows {
  grid-template-columns: repeat(2, minmax(240px, 1fr));
  gap: 10px;
}
.preview-matches.grid-4-wide {
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 8px;
}
.preview-matches.grid-auto {
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
}
.preview-group {
  border: 1px solid #ced4da;
  border-radius: 10px;
  padding: 10px;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.preview-group-header {
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: #fff;
  background: #007bff;
  padding: 6px 10px;
  border-radius: 8px;
}
.preview-group-matches {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.preview-match-card {
  position: relative;
  border: 1px solid #ced4da;
  border-radius: 10px;
  padding: 18px 12px 12px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.04);
}
.preview-match-title {
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  background: #007bff;
  color: #fff;
  padding: 4px 10px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 12px;
}
.preview-player-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.preview-player {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.preview-avatar-wrapper {
  position: relative;
}
.preview-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 2px solid #ccc;
  object-fit: cover;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.4);
}
.preview-avatar.winner {
  border-color: #0e7c66;
  box-shadow: 0 0 10px rgba(14, 124, 102, 0.7), 0 0 20px rgba(14, 124, 102, 0.45);
  animation: previewWinnerPulse 1.5s infinite ease-in-out;
}
@keyframes previewWinnerPulse {
  0% { box-shadow: 0 0 5px rgba(14, 124, 102, 0.65), 0 0 10px rgba(14, 124, 102, 0.4); }
  50% { box-shadow: 0 0 20px rgba(14, 124, 102, 0.85), 0 0 40px rgba(14, 124, 102, 0.55); }
  100% { box-shadow: 0 0 5px rgba(14, 124, 102, 0.65), 0 0 10px rgba(14, 124, 102, 0.4); }
}
.player-name {
  font-weight: 700;
  color: #2f365f;
  max-width: 120px;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.score-chip {
  background: #ffc107;
  color: #2a1f04;
  padding: 6px 12px;
  border-radius: 10px;
  font-weight: 700;
  min-width: 74px;
  text-align: center;
  border: 1px solid #e0aa06;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.4);
}
.preview-vs {
  font-weight: 800;
  color: #ff0000;
  padding: 0 6px;
  font-size: 18px;
}
.type-chip {
  padding: 2px 8px;
  border-radius: 999px;
  background: #eef0ff;
  color: #3d3a7b;
  font-weight: 700;
  font-size: 12px;
}
.preview-body.vs {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
}
.preview-match {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 10px;
}
.match-label {
  font-weight: 700;
  margin-bottom: 6px;
}
.match-seeds {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.seed {
  padding: 6px 10px;
  border-radius: 6px;
  background: #f5f7ff;
  font-weight: 700;
  color: #333;
}
.vs-tag {
  font-weight: 800;
  color: #6c63ff;
}
.preview-body.scoring {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.scoring-row {
  display: grid;
  grid-template-columns: 40px 56px 1fr 80px 90px;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid #e2e6ef;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 4px 10px rgba(0,0,0,0.03);
}
.scoring-row.advance {
  border-color: #4caf50;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.15);
}
.scoring-row.out {
  opacity: 0.82;
}
.rank {
  font-weight: 800;
  color: #2f365f;
  text-align: center;
}
.name {
  color: #333;
}
.scoring-badge {
  justify-self: end;
  padding: 4px 8px;
  border-radius: 999px;
  background: #e7f7ec;
  color: #1f7a4d;
  font-weight: 700;
  font-size: 12px;
  border: 1px solid #b9e3c9;
}
.scoring-badge.muted {
  background: #f4f4f6;
  color: #6b7280;
  border-color: #e0e1e6;
}
.scoring-badge.neutral {
  background: #eef2ff;
  color: #374151;
  border-color: #d1d5db;
}
.scoring-avatar {
  display: flex;
  justify-content: center;
}
.scoring-img {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
  border: 2px solid #d6d9e5;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.4);
}
.scoring-name {
  font-weight: 700;
  color: #2f365f;
}
.scoring-score {
  font-weight: 700;
  color: #1f7a4d;
  text-align: right;
}
.scoring-toggle {
  margin-top: 6px;
  align-self: flex-end;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid #cfd5ff;
  background: #f4f6ff;
  color: #304286;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
}
.scoring-toggle:hover {
  background: #e8ebff;
}

@media (max-width: 600px) {
  .preview-backdrop {
    align-items: flex-start;
    padding: 64px 10px 12px; /* empuja el modal hacia el top en móvil */
  }
  .preview-modal {
    width: 100%;
    border-radius: 16px 16px 0 0;
    padding: 12px;
    gap: 10px;
    max-height: 88vh;
  }
  .preview-modal-header {
    gap: 8px;
  }
  .preview-title {
    font-size: 16px;
  }
  .preview-stage-card {
    gap: 10px;
    padding: 10px 10px 12px;
  }
  .preview-stage-header {
    flex-wrap: wrap;
    gap: 8px;
  }
  .preview-meta {
    flex-wrap: wrap;
    row-gap: 6px;
  }
  .preview-bracket__stack--split,
  .preview-bracket__layout--paired {
    grid-template-columns: 1fr;
  }
  .preview-bracket__match-group,
  .preview-bracket__match-group--right {
    grid-template-columns: 1fr;
  }
  .preview-bracket__connector {
    display: none;
  }
  .preview-bracket__slot {
    grid-template-columns: auto minmax(0, 1fr) auto;
    gap: 0.45rem;
    padding: 0.45rem 0.6rem;
  }
  .preview-bracket__avatar-wrap {
    width: 2.2rem;
    height: 2.2rem;
    flex-basis: 2.2rem;
  }
  .preview-bracket__slot-score {
    grid-column: auto;
    margin-left: auto;
    text-align: right;
    min-width: 2.6rem;
    font-size: 0.76rem;
  }
  .preview-bracket__slot-name {
    white-space: nowrap;
    font-size: 0.76rem;
  }
  .preview-bracket__slot-copy {
    flex-wrap: nowrap;
    align-items: center;
    overflow: hidden;
    gap: 0.28rem;
  }
  .preview-bracket__status-pill {
    min-height: 1.25rem;
    padding: 0.12rem 0.45rem;
    font-size: 0.58rem;
    flex: 0 0 auto;
  }
  .preview-matches,
  .preview-matches.grid-2,
  .preview-matches.grid-4,
  .preview-matches.grid-2-rows,
  .preview-matches.grid-4-wide,
  .preview-matches.grid-auto {
    grid-template-columns: 1fr;
  }
  .preview-group {
    gap: 6px;
  }
  .preview-group-matches {
    gap: 10px;
  }
  .preview-match-card {
    padding: 14px 10px 10px;
  }
  .preview-match-title {
    font-size: 11px;
    padding: 4px 8px;
  }
  .preview-player-container {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }
  .preview-player {
    align-items: center;
    flex: 1;
    min-width: 140px;
  }
  .preview-avatar {
    width: 52px;
    height: 52px;
  }
  .player-name {
    max-width: none;
    font-size: 13px;
  }
  .score-chip {
    min-width: 64px;
    padding: 5px 10px;
    font-size: 13px;
  }
  .preview-vs {
    margin: 4px 0;
  }
  .scoring-row {
    grid-template-columns: 32px 44px 1fr;
    grid-template-areas:
      "rank avatar name"
      "score score badge";
    align-items: center;
  }
  .rank { grid-area: rank; }
  .scoring-avatar { grid-area: avatar; }
  .scoring-name { grid-area: name; }
  .scoring-score {
    grid-area: score;
    text-align: left;
  }
  .scoring-badge {
    grid-area: badge;
    justify-self: end;
  }
  .scoring-img {
    width: 42px;
    height: 42px;
  }
  .round-sizes-carousel {
    display: flex;
    flex-wrap: nowrap;
    gap: 10px;
    padding: 6px 4px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
  }
  .round-sizes-carousel::-webkit-scrollbar {
    display: none;
  }
  .stage-card {
    flex: 0 0 82vw;
    min-width: 0;
    scroll-snap-align: start;
  }
  .carousel-shell {
    gap: 8px;
  }
  .nav-btn {
    width: 34px;
    height: 34px;
  }
}
</style>
